import {useEffect} from 'react';
import {LngLatBoundsLike} from 'mapbox-gl';
import {useWindowSize} from 'react-use';

import {hexToRectangle} from '~utils/plot/coordinates';
import {joinWholeAndDecimal} from '~utils/plot/geoJson';
import {PlotCoordinate, PlotsListResponse} from '~types/Plots';
import {filtersMenuWidth} from '~molecules/FiltersMenu/styles';
import {defaultBounds} from '~constants/map';
import {MapRef} from '~types/Map';
import {disableMap, enableMap} from '~utils/map/mapMovement';
import {MapLayer} from '~constants/plots';

export default function useCenterMap(
    map: MapRef,
    data: PlotsListResponse | undefined,
    filteredPlots: number[] | null | undefined,
    selectedPlot: number | undefined,
) {
    const mapLoaded = Boolean(map.current?.getLayer(MapLayer.HexFill));
    const {width} = useWindowSize();

    useEffect(() => {
        if (!map.current) return;
        if (mapLoaded) {
            enableMap(map.current);
        } else {
            disableMap(map.current);
        }
    }, [mapLoaded, map.current]);

    useEffect(() => {
        if (!mapLoaded || !data?.plots || !filteredPlots?.length || selectedPlot) {
            return;
        }

        if (filteredPlots?.length) {
            const filteredPlotsWithCords = data.plots
                .filter(({i}) => filteredPlots.includes(i))
                .map(({c}) => c);

            const rectangle = hexToRectangle(filteredPlotsWithCords);

            const bounds = [
                [rectangle[0][0], rectangle[2][1]],
                [rectangle[2][0], rectangle[0][1]],
            ].map((d) => joinWholeAndDecimal(data.coordinate, d as PlotCoordinate));

            const padding = 50;
            map.current?.fitBounds(bounds as LngLatBoundsLike, {
                animate: true,
                padding:
                    width < 768
                        ? 25
                        : {
                              top: padding,
                              left: padding + filtersMenuWidth,
                              right: padding,
                              bottom: padding,
                          },
                maxZoom: 18,
            });
        } else {
            map.current?.fitBounds(defaultBounds, {animate: true});
        }
    }, [filteredPlots]);
}
